import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { getTemplateById, getTemplates } from "services/headwallServices";
import { errorAlert } from "config/sweet-alert/alert";
import { AddIcon } from "assets/images";
import { PRODUCT } from "constants/appConstant";
import {
  convertInchesToFeetAndInches,
  headwallData,
} from "config/calculations/calculation";
import { useHeadWallStore, useLoaderStore, useRightPanelStore } from "store";

function Templates({ setShowTempWarning }) {
  const [tempData, setTempData] = useState([]);
  const { setLoading } = useLoaderStore();
  const {
    values,
    setValues,
    resetState: resetHeadwallData,
  } = useHeadWallStore();
  const { resetState, updateStepCompletion } = useRightPanelStore();
  const { model, sub_model, name } = values;
  //function mutation
  const { isLoading } = useQuery({
    queryKey: ["templates", model, sub_model],
    queryFn: () => getTemplates(model),
    refetchOnWindowFocus: false,
    enabled: !!model,
    onSuccess: (data) => {
      const res = data?.data?.data;
      setTempData(res);
      if (
        model === PRODUCT.AXIOM ||
        model === PRODUCT.CONSOLE ||
        model === PRODUCT.ELOQUENCE||
        model === PRODUCT.SECURITY ||
        model === PRODUCT.HIDDEN
      ) {
        const templates = res.filter((temp) => temp?.sub_model === sub_model);
        setTempData(templates);
      } else setTempData(res);
    },
  });

  //manage loader
  useEffect(() => {
    setLoading(isLoading);
    return () => {
      setLoading(false);
    };
  }, [isLoading, setLoading]);

  const handleSelectTemplate = async (design_id) => {
    try {
      const { data } = await getTemplateById(design_id);
      const tempData = data.data;
      const { width, ceiling_height, headwalls: sections, model,sub_model } = tempData;
      const headwallWidth = convertInchesToFeetAndInches(width,model,sub_model);
      const headwallHeight = convertInchesToFeetAndInches(ceiling_height);
      const headwalls = headwallData(sections, model);
      setValues({
        ...tempData,
        width: headwallWidth,
        step: 3,
        ceiling_height: headwallHeight,
        ...headwalls,
      });
      updateStepCompletion(3, true);
      setShowTempWarning(true);
    } catch (err) {
      errorAlert(err?.response?.data?.message);
    }
  };

  const handleCustomClick = () => {
    resetHeadwallData();

    const headwalltype = model;
    const baseHeadwall = {
      position: "",
      services: [],
      width: 0,
      color: "",
      background: "7953",
    };

    const baseValues = {
      model: headwalltype,
      name: name,
      width: { ft: 0, in: 0, ftError: "" },
      headwalls: [{ ...baseHeadwall }],
      step: 3,
    };

    switch (headwalltype) {
      case PRODUCT.INFINITY:
        baseValues.width.ft = 3;
        baseValues.headwalls[0].position = "middle";
        break;

      case PRODUCT.AXIOM:
        baseValues.width.ft = 2;
        baseValues.headwalls[0].position = "middle";
        baseValues.sub_model = sub_model;
        break;

      case PRODUCT.COMPASS:
        baseValues.headwalls[0].width = 12;
        break;

      case PRODUCT.ARRAY:
        baseValues.headwalls[0].width = 2;
        break;

      case PRODUCT.CONSOLE:
        baseValues.width.in = 35;
        baseValues.headwalls[0].position = "middle";
        baseValues.sub_model = sub_model;
        break;

      case PRODUCT.ELOQUENCE:
        baseValues.width.ft = 2;
        baseValues.width.in = 6;
        baseValues.headwalls = [
          { ...baseHeadwall, services: [] },
          { ...baseHeadwall, services: [] },
        ];
        baseValues.sub_model = sub_model;
        break;

      case PRODUCT.SECURITY:
        switch (sub_model) {
          case "surface_monuted":
            baseValues.width.ft = 0;
            baseValues.width.in = 14.125;
            break;
          case "insight":
            baseValues.width.ft = 0;
            baseValues.width.in = 17.125;
            break;
          case "enclosure":
            baseValues.width.ft = 0; // or whatever value
            baseValues.width.in = 8; // or whatever value
            break;
          default:
            baseValues.width.in = 14.125; // fallback
        }
        baseValues.sub_model = sub_model;
        // Override baseHeadwall color + background
        baseValues.headwalls = [
          {
            ...baseHeadwall,
            color: "#a5a5a5",
            background: "",
          },
        ];
        break;

      case PRODUCT.HIDDEN:
        switch (sub_model) {
          case "single":
            baseValues.width.in = 24;
            break;
          case "double":
            baseValues.width.in = 48;
            break;
          default:
            baseValues.width.in = 24; // fallback
        }
        baseValues.width.ft = 0;
        // baseValues.width.in = 24;
        baseValues.headwalls = [
          { ...baseHeadwall, services: [] },
          { ...baseHeadwall, services: [] },
          { ...baseHeadwall, services: [] },
        ];
        baseValues.sub_model = sub_model;
        baseValues.add_rail = "right";
        break;

      default:
        // Handle unknown types gracefully if needed
        break;
    }

    setValues(baseValues);
    resetState();
    updateStepCompletion(3, true);
  };

  return (
    <>
      {!isLoading && (
        <div
          className="grid grid-cols-2 gap-4 h-full"
          style={{ gridTemplateRows: `calc(50% - 8px) calc(50% - 8px)` }}
        >
          {tempData.map((temp) => (
            <div key={temp._id}>
              <div className="flex justify-end font-bold text-base">
                {temp.name}
              </div>
              <div
                className=" bg-[hsla(210,6%,73%,.3)] border rounded-lg cursor-pointer w-full"
                style={{ height: "calc(100% - 24px)" }}
                onClick={() => handleSelectTemplate(temp._id)}
              >
                <img
                  src={temp.design_image_url}
                  alt="...img"
                  loading="lazy"
                  className="h-full object-contain w-full place-items-center"
                />
              </div>
            </div>
          ))}
          <div>
            <div className="flex justify-end">&nbsp;</div>
            <div
              className={`bg-[#FFE8C9] rounded-lg flex justify-center items-center flex-col cursor-pointer w-full`}
              onClick={() => handleCustomClick()}
              style={{ height: "calc(100% - 24px)" }}
            >
              <img
                src={AddIcon}
                alt="...img"
                loading="lazy"
                className="w-[90px] h-[90px]"
              />
              <h5 className="mt-5 text-3xl font-extrabold text-[#8E5359]">
                Create Custom Design
              </h5>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Templates;
